import type {
  AttributeValue,
  MeterRead,
  SupplyPointSummary,
  TimelineEntry,
} from "@riea-water/contracts";

/** The prototype's confidence colour: green from 85, amber from 65, red below. */
export function confidenceColour(score: number): string {
  return score >= 85 ? "var(--ok)" : score >= 65 ? "var(--warn)" : "var(--crit)";
}

export type ConfidenceBand = "" | "low" | "mid" | "high";

export function bandOf(band: ConfidenceBand): { min?: number; max?: number } {
  switch (band) {
    case "low":
      return { max: 64 };
    case "mid":
      return { min: 65, max: 84 };
    case "high":
      return { min: 85 };
    default:
      return {};
  }
}

/** "Today", "41 days ago", "never read", red beyond a year (the prototype's last actual column). */
export function lastActualLabel(
  iso: string | null,
  now = new Date(),
): { label: string; overdue: boolean } {
  if (!iso) return { label: "never read", overdue: true };
  const days = Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return { label: "Today", overdue: false };
  return { label: `${days} days ago`, overdue: days > 365 };
}

export function readMethodLabel(method: SupplyPointSummary["readMethod"]): string {
  return method === "smart" ? "Smart" : method === "amr" ? "AMR" : "Manual";
}

/** The pill for one attribute value, as the prototype words it. */
export function attributePill(
  values: readonly AttributeValue[],
  attribute: string,
): { tone: string; label: string } {
  const top = values[0];
  if (!top) return { tone: "warn", label: "Missing" };
  if (top.inConflict) return { tone: "crit", label: "Conflict" };
  if (top.value === null || top.value === "" || top.value === "unknown")
    return { tone: "warn", label: "Missing" };
  if (top.source === "field") return { tone: "ok", label: "Evidenced" };
  if (top.source === "market") return { tone: "ok", label: "Linked" };
  if (attribute === "uprn") return { tone: "ok", label: "Matched" };
  if (top.confidence < 0.5) return { tone: "warn", label: "Vague" };
  return { tone: "ok", label: top.source === "import" ? "Imported" : "Current" };
}

/** "Import · line 4,118 · 9 Oct 2026" style provenance line. */
export function provenanceLine(v: AttributeValue): string {
  const when = new Date(v.observedAt).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/London",
  });
  const source =
    v.source === "import"
      ? "Import"
      : v.source === "field"
        ? "Field record"
        : v.source === "market"
          ? "Market"
          : v.source === "user"
            ? "User"
            : v.source === "rule"
              ? "Rule"
              : "System";
  const ref = v.sourceRef.replace(
    /^import:[^#]+#(\d+)$/,
    (_m, line: string) => `line ${Number(line).toLocaleString("en-GB")}`,
  );
  return `${source} · ${ref} · ${when}`;
}

export function valueText(value: unknown): string {
  if (value === null || value === undefined || value === "") return "Not held";
  if (Array.isArray(value)) return value.map(String).join(", ");
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint")
    return String(value);
  return JSON.stringify(value);
}

/** Monthly consumption from a series of register reads: the difference between the last read of consecutive months. */
export function monthlyConsumption(
  reads: readonly MeterRead[],
  months: number,
  asOf: Date,
): { month: string; value: number; estimated: boolean }[] {
  const sorted = [...reads].sort((a, b) => a.readAt.localeCompare(b.readAt));
  const out: { month: string; value: number; estimated: boolean }[] = [];
  for (let m = months - 1; m >= 0; m -= 1) {
    const end = new Date(Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth() - m + 1, 1));
    const start = new Date(Date.UTC(asOf.getUTCFullYear(), asOf.getUTCMonth() - m, 1));
    const inMonth = sorted.filter((r) => new Date(r.readAt) >= start && new Date(r.readAt) < end);
    const before = sorted.filter((r) => new Date(r.readAt) < start).at(-1);
    const last = inMonth.at(-1);
    const value = last && before ? Math.max(0, last.value - before.value) : 0;
    out.push({
      month: start.toLocaleDateString("en-GB", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      }),
      value: Math.round(value * 10) / 10,
      estimated: inMonth.length > 0 && inMonth.every((r) => r.readType === "estimated"),
    });
  }
  return out;
}

export function timelineDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/London",
  });
}

/** A one-line description of a timeline entry when the API gives none. */
export function timelineSummary(entry: TimelineEntry): string {
  if (entry.summary) return entry.summary;
  const p = entry.event?.payload ?? {};
  switch (entry.type) {
    case "MeterInstalled":
      return `Meter ${valueText(p["serial"])} installed`;
    case "ReadRecorded":
      return `Read ${valueText(p["value"])} recorded`;
    case "OccupancyChanged":
      return `Occupancy ${valueText(p["from"])} to ${valueText(p["to"])}`;
    case "MeterAttributeChanged":
    case "SupplyPointAttributeChanged":
    case "PremisesAttributeChanged":
      return `${valueText(p["attribute"]).replace(/_/g, " ")} changed to ${valueText(p["new"])}`;
    case "AttributeConflictDetected":
      return `${valueText(p["attribute"])} conflict between sources`;
    case "FieldJobCompleted":
      return "Field job completed";
    case "FieldJobRaised":
      return `Field job raised (${valueText(p["type"])})`;
    default:
      return entry.type.replace(/([a-z])([A-Z])/g, "$1 $2");
  }
}
